import { useEffect, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { GameButton } from '@/components/ui/GameButton'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { HomeEmberScene } from '@/components/game3d/HomeEmberScene'
import { getCharacter } from '@/game/characters'
import type { PixelIconName } from '@/game/pixelIcons'

const PREMISE: { icon: PixelIconName; text: string }[] = [
  { icon: 'levelSelect', text: 'Slide Ember across each board. It drifts until it hits a wall.' },
  { icon: 'home', text: 'Reach the warm lit window to carry Ember home.' },
  { icon: 'trophy', text: 'Fewer slides, more stars. Six biomes, one road home.' },
]

const ART = `${import.meta.env.BASE_URL}art/story_00_home_hearth.webp`

export function BootScreen() {
  const { go } = useNavigation()
  const { save, loaded } = useProgress()
  const reduced = useReducedMotion()
  const [ready, setReady] = useState(false)

  const character = getCharacter(save.cosmetics.equippedCharacter)
  const accent = character && character.id !== 'default_hero' ? character.palette.base : '#ffb454'
  const emissive = character && character.id !== 'default_hero' ? character.palette.emissive : '#ffd27a'

  useEffect(() => {
    if (loaded) {
      const t = setTimeout(() => setReady(true), 350)
      return () => clearTimeout(t)
    }
  }, [loaded])

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <img
        src={ART}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none fixed inset-0 h-[100dvh] w-[100vw] object-cover"
        style={{ zIndex: 0 }}
      />
      <div
        className="pointer-events-none fixed inset-0"
        style={{ background: 'linear-gradient(180deg, rgba(10,8,16,0.55) 0%, rgba(10,8,16,0.35) 40%, var(--ink) 92%)', zIndex: 0 }}
        aria-hidden="true"
      />

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-6 px-6 py-8 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="relative h-40 w-40"
          style={{ filter: `drop-shadow(0 12px 40px ${accent}66)` }}
        >
          <HomeEmberScene color={accent} emissive={emissive} shape={character?.shape} reducedMotion={!!reduced} characterId={character?.id ?? 'default_hero'} />
        </motion.div>

        <div className="max-w-sm">
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
            className="font-display text-4xl font-black tracking-tight text-balance"
          >
            Take Me Home
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="mt-2 text-[var(--text-dim)] text-pretty"
          >
            A little flame, torn from home by the first cold wind. One breath, one path back to the light.
          </motion.p>
        </div>

        <motion.ul
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: 0.35 } } }}
          className="flex w-full max-w-sm flex-col gap-2.5 text-left"
        >
          {PREMISE.map((p) => (
            <motion.li
              key={p.text}
              variants={{ hidden: { opacity: 0, x: -12 }, show: { opacity: 1, x: 0 } }}
              transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
              className="flex items-center gap-3 rounded-[var(--r-md)] border border-[var(--edge)]/40 bg-[var(--surface)]/70 px-3.5 py-2.5 backdrop-blur-sm"
            >
              <span
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full"
                style={{ background: `${accent}22`, color: accent }}
              >
                <PixelIcon name={p.icon} size={16} />
              </span>
              <span className="text-sm text-[var(--text)]">{p.text}</span>
            </motion.li>
          ))}
        </motion.ul>
      </div>

      <div className="relative z-10 px-6 pb-[calc(2rem+var(--safe-bottom))]">
        <div className="mx-auto w-full max-w-sm">
          {ready ? (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <GameButton size="lg" icon="play" fullWidth onClick={() => go('home')}>
                Begin the Journey
              </GameButton>
            </motion.div>
          ) : (
            <div className="flex items-center justify-center gap-2 py-4 text-[var(--text-faint)]">
              <motion.span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: accent }}
                animate={reduced ? undefined : { opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
              <span className="text-sm">Warming the hearth</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
